import has from 'lodash/has';
import set from 'lodash/set';
import { BorderSketchType } from '@src/slide/domain/ui-model/element-modeles/property/border/constants';
import { PathProcessor } from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/path-processor';
import { FillMode, GraphicsType, ICustomPath } from '@tencent/alloy-graphics';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';

export default class SketchUpdateAndRemove {
    /**
     * 草绘方式改变后更新数据
     * 草绘–>非草绘，根据 sketchStyle 中 bgShape，还原元素类型，删除自定义 pathList;
     * 非草绘–>草绘，在 sketchStyle 中记录元素 bgShape，根据草绘方式生成自定义 pathList;
     * @param model
     * @param uiProps
     * @param updateAndRemove
     */
    public static updateSketchUpdateAndRemoveProps(model: IElementBaseModel, uiProps, updateAndRemove) {
        if (!this.shouldUpdate(model, uiProps)) {
            return;
        }
        const { bgShape, controlValues, pathList, border } = (model as IShapeModel).props?.style || {};
        const { type = BorderSketchType.NONE } = uiProps.style.border.sketchStyle || {};
        if (type === BorderSketchType.NONE) {
            // 设置无草绘后需要删除边框的 sketchStyle 属性，并根据 sketchStyle 中记录的元素类型更新 bgShape
            const bgShape = border?.sketchStyle?.bgShape;
            set(updateAndRemove, 'update.style.bgShape', bgShape);
            set(updateAndRemove, 'update.style.border.sketchStyle', {
                type,
                bgShape: this.getBgShape(model, bgShape),
                controlValues: controlValues ?? [],
                customGeom: this.getCustomGeom(model, this.getPathList(model, bgShape, pathList as ICustomPath[])),
            });
            // 如果元素的原始类型是 custom，需要根据 sketchStyle 中记录的 pathList 更新元素 customGeom
            if (bgShape === GraphicsType.CUSTOM) {
                const customGeom = SketchUpdateAndRemove.getCustomGeom(model, border?.sketchStyle?.pathList);
                set(updateAndRemove, 'update.style.customGeom', customGeom);
            } else {
                set(updateAndRemove, 'update.style.customGeom', null);
                set(updateAndRemove, 'remove.style.customGeom', null);
            }
        } else {
            set(updateAndRemove, 'update.style.bgShape', GraphicsType.CUSTOM);
            const newPathList = PathProcessor.generateSketchPathList({
                model: model as IShapeModel,
                type,
            });
            set(updateAndRemove, 'update.style.customGeom', SketchUpdateAndRemove.getCustomGeom(model, newPathList));
            set(updateAndRemove, 'update.style.border.sketchStyle', {
                type,
                bgShape: this.getBgShape(model, bgShape),
                controlValues: controlValues ?? [],
                customGeom: this.getCustomGeom(model, this.getPathList(model, bgShape, pathList as ICustomPath[])),
            });

            // 设置草绘格式后需要删除元素 prstGeomType 属性，否则 prstGeomType 和 customGeom 共存导出会报错
            set(updateAndRemove, 'remove.style.bgShape', bgShape);
            // 元素原来的 sketchStyle 中的 bgShape 为 custom，并且草绘类型为 none 时，需要移除 customGeom
            if (border?.sketchStyle?.bgShape === GraphicsType.CUSTOM
                && border?.sketchStyle?.type === BorderSketchType.NONE) {
                set(updateAndRemove, 'remove.style.border.sketchStyle.bgShape', GraphicsType.CUSTOM);
                set(updateAndRemove, 'remove.style.border.sketchStyle.customGeom', null);
            }
        }
    }

    public static getCustomGeom(model: IElementBaseModel, pathList?: ICustomPath[]) {
        if (!pathList) {
            return;
        }
        let deleteArr: any[] = [];
        const prevPathList = (model as IShapeModel).props.style.pathList as ICustomPath[];
        if (prevPathList) {
            // 更新后的 pathList 长度小于原 pathList 长度，需要将删除的 path 设置为 null，否则多余 path 不会被删除
            const length = Math.max(prevPathList.length - pathList.length, 0);
            deleteArr = Array(length).fill(null);
        }
        pathList.forEach((path, index) => {
            if (!path) return;
            // 更新后的 path 如果不存在 stroke 或者 fill，需要使用默认值更新对应属性
            if (!has(path, 'stroke') && has(prevPathList[index], 'stroke')) {
                path.stroke = 1;
            }
            if (!has(path, 'fill') && has(prevPathList[index], 'fill')) {
                path.fill = FillMode.NORMAL;
            }
        });

        return {
            pathList: [...pathList, ...deleteArr],
            rect: {
                b: 'b',
                l: 'l',
                r: 'r',
                t: 't',
            },
        };
    }

    private static shouldUpdate(model: IElementBaseModel, uiProps) {
        const { border } = (model as IShapeModel).props?.style || {};
        const { sketchStyle } = uiProps.style.border;
        if (!sketchStyle) return;
        return !((model.type !== UIModelType.IMAGE && model.type !== UIModelType.SHAPE)
            || border?.sketchStyle?.type === sketchStyle?.type
            || (!border?.sketchStyle?.type && sketchStyle?.type === BorderSketchType.NONE)
            || (!sketchStyle?.type && border?.sketchStyle?.type === BorderSketchType.NONE));
    }

    private static getBgShape(model: IElementBaseModel, bgShape?: string): string {
        const { border } = model.props?.style || {};
        // 草绘类型为 none 时, 直接使用元素 style 中的 bgShape，其他情况优先使用 sketchStyle 中的 bgShape
        return (border?.sketchStyle?.type === BorderSketchType.NONE
            ? bgShape : border?.sketchStyle?.bgShape ?? bgShape) ?? GraphicsType.RECT;
    }

    private static getPathList(
        model: IElementBaseModel,
        bgShape?: string,
        pathList?: ICustomPath[],
    ): ICustomPath[] | undefined {
        const { border } = model.props?.style || {};
        // 设置草绘后，需要将元素类型、控制点、自定义图形的 pathList 信息 记录在 sketchStyle 中
        // 草绘类型为 none 时, 不需要记录 pathList
        return (border?.sketchStyle?.bgShape ?? bgShape) === GraphicsType.CUSTOM
        && border?.sketchStyle?.type !== BorderSketchType.NONE
            ? (border?.sketchStyle?.pathList ?? pathList) : undefined;
    }
}
